import { useEffect, useId, useRef, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Alert } from './alert';
import { Button } from './button';
import type { ButtonVariant } from './button-class';
import { Icon, type IconName } from './icon';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
export type ModalVariant = 'default' | 'danger' | 'warning';

/** Bootstrap's `modal-sm` / default / `modal-lg` are 300 / 600 / 900px; the wider sizes are React's. */
const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-[300px]',
  md: 'max-w-[600px]',
  lg: 'max-w-[900px]',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  full: 'max-w-[95vw]',
};

const SUBMIT: Record<ModalVariant, ButtonVariant> = {
  default: 'primary',
  danger: 'danger',
  warning: 'warning',
};

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * A dialog with a title, a body, and Cancel / Submit — the frame of every form modal (portal-ui-lib's
 * `FormModalForm` + `FormModalContent`). The body is a `<form>`, so Enter submits. Escape and a
 * click on the backdrop close it; focus stays inside while open and returns where it was after.
 *
 * Drawn as the legacy Bootstrap modal: top-aligned, small buttons in a left-aligned footer, cancel
 * (thumbs-down) before confirm (thumbs-up) — except `danger`, which puts the red delete first.
 */
export function Modal({
  open,
  title,
  onClose,
  onSubmit,
  submitLabel,
  submittingLabel,
  cancelLabel,
  submitting = false,
  submitDisabled = false,
  error,
  size = 'md',
  variant = 'default',
  closeOnOverlay = true,
  cancelIcon,
  submitIcon,
  submitVariant,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  /** Without it the dialog has only a close button (informational). */
  onSubmit?: () => void;
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  /** Shown above the footer. */
  error?: string | null;
  size?: ModalSize;
  variant?: ModalVariant;
  closeOnOverlay?: boolean;
  /** Footer icons, when a dialog's legacy original used others than thumbs down / up. */
  cancelIcon?: IconName;
  submitIcon?: IconName;
  /**
   * The confirm button's colour without `variant`'s button order — for a delete that legacy put
   * after its cancel.
   */
  submitVariant?: ButtonVariant;
  children: ReactNode;
}) {
  const t = useShellTexts().kit;
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.activeElement as HTMLElement | null;
    const first =
      panel.current?.querySelector<HTMLElement>('input, select, textarea') ?? panel.current;
    first?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) {
    return null;
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!submitting && !submitDisabled) {
      onSubmit?.();
    }
  };

  const cancel = (
    <Button
      key="cancel"
      size="sm"
      icon={cancelIcon ?? (onSubmit ? 'thumbs-down' : 'close')}
      onClick={onClose}
    >
      {cancelLabel ?? t.cancel}
    </Button>
  );
  const confirm = onSubmit ? (
    <Button
      key="confirm"
      type="submit"
      size="sm"
      variant={submitVariant ?? SUBMIT[variant]}
      icon={submitIcon ?? (variant === 'danger' ? 'trash' : 'thumbs-o-up')}
      loading={submitting}
      disabled={submitDisabled}
    >
      {submitting
        ? (submittingLabel ?? t.saving)
        : (submitLabel ?? (variant === 'danger' ? t.delete : t.save))}
    </Button>
  ) : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/50 px-4 pt-[30px] pb-4"
      onMouseDown={event => {
        if (closeOnOverlay && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`flex max-h-[calc(100vh-60px)] w-full flex-col rounded-bs-lg border border-black/20 bg-white bg-clip-padding text-ink shadow-[0_5px_15px_rgba(0,0,0,.5)] ${SIZES[size]}`}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
          } else if (event.key === 'Tab' && panel.current) {
            const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
              element => !element.hasAttribute('disabled'),
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <div className="flex items-center justify-between gap-3 rounded-t-bs-lg border-b border-line bg-cap px-5 py-3">
          <h2 id={titleId} className="mb-0 text-sm leading-normal font-bold">
            {title}
          </h2>
          <button
            type="button"
            aria-label={t.close}
            onClick={onClose}
            className="text-base leading-none text-ink opacity-50 hover:opacity-75"
          >
            <Icon name="close" />
          </button>
        </div>
        <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-5">{children}</div>
          {error ? (
            <div className="mx-5">
              <Alert tone="danger">{error}</Alert>
            </div>
          ) : null}
          <div className="flex justify-start gap-2 rounded-b-bs-lg border-t border-line bg-cap px-5 py-3">
            {variant === 'danger' ? [confirm, cancel] : [cancel, confirm]}
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
